import { IconButton, SvgIconTypeMap, Tooltip, styled } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Actions from 'components/Actions';
import Action from 'components/Actions/Action';
import { Row } from 'components/Layout';
import { isString, once } from 'lodash';
import React, { ComponentType } from 'react';
import { Link } from 'react-router-dom';

export type TableAction = {
  label?: string;
  icon?: OverridableComponent<SvgIconTypeMap<object>>;
  link?: string | { pathname: string; state: Record<string, any> };
  external?: boolean;
  onClick?: (e?: React.MouseEvent, closeMenu?: () => void) => void;
  disabled?: boolean;
  visible?: boolean;
  color?: 'primary' | 'error' | 'default';
  tooltip?: string;
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  loading?: boolean;
  actionComponent?: ComponentType<{
    onClick?: () => void;
    disabled?: boolean;
    closeDropdown?: () => void;
  }>;
};

type Props = {
  showAsDropdown?: boolean;
  stopPropagation?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  actions: Array<TableAction>;
};

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing(5),
}));

const ActionsWrapper: React.FC<{
  showAsDropdown?: boolean;
  stopPropagation?: boolean;
  children: React.ReactNode | ((closeMenu: () => void) => React.ReactNode);
}> = ({ children, showAsDropdown, stopPropagation }) =>
  showAsDropdown ? (
    <Actions stopPropagation={stopPropagation}>{children}</Actions>
  ) : (
    <Row justify="flex-end" centered>
      {typeof children === 'function' ? children(() => {}) : children}
    </Row>
  );

const TableActions: React.FC<Props> = ({
  actions,
  showAsDropdown,
  stopPropagation,
  className,
  size = 'large',
}) => {
  if (!actions.some((action) => action.visible !== false)) {
    return <EmptyState>-</EmptyState>;
  }

  return (
    <div className={className}>
      <ActionsWrapper
        showAsDropdown={showAsDropdown}
        stopPropagation={stopPropagation}
      >
        {(closeMenu) => (
          <>
            {actions.map(
              (
                {
                  label,
                  color,
                  icon: Icon,
                  link,
                  onClick,
                  visible = true,
                  disabled,
                  tooltip,
                  external,
                  tooltipPlacement,
                  loading,
                  actionComponent: ActionComponent,
                },
                index,
              ) => {
                if (!visible) return null;
                const key = `table-actions-${index}`;
                const onClickHandler =
                  link || disabled || loading ? undefined : onClick;
                // In dropdown mode: pass (event, closeMenuOnce) so handlers get the real event first.
                // closeMenuOnce runs the real close at most once (we call it after; handler may call it after async).
                const wrappedOnClickHandler =
                  onClickHandler && showAsDropdown
                    ? (e?: React.MouseEvent) => {
                        e?.stopPropagation();
                        const closeMenuOnce = once(closeMenu);
                        try {
                          onClickHandler(e, closeMenuOnce);
                        } finally {
                          closeMenuOnce();
                        }
                      }
                    : onClickHandler;

                const button = showAsDropdown ? (
                  ActionComponent ? (
                    <ActionComponent
                      onClick={onClickHandler}
                      disabled={!!disabled}
                      closeDropdown={closeMenu}
                      key={key}
                    />
                  ) : (
                    <Action
                      key={key}
                      label={label ?? ''}
                      icon={Icon}
                      color={color}
                      disabled={disabled}
                      loading={loading}
                      onClick={wrappedOnClickHandler}
                    />
                  )
                ) : (
                  <IconButton
                    key={key}
                    title={tooltip ? undefined : label}
                    color={color}
                    size={size}
                    onClick={onClickHandler}
                    disabled={disabled || loading}
                  >
                    {Icon && <Icon />}
                  </IconButton>
                );

                const linkComponent =
                  external && isString(link) ? (
                    <a
                      key={key}
                      href={link}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {button}
                    </a>
                  ) : (
                    <Link key={key} to={link ?? '#'}>
                      {button}
                    </Link>
                  );

                const action = link && !disabled ? linkComponent : button;

                return tooltip ? (
                  <Tooltip
                    title={tooltip}
                    key={`table-actions-${index}`}
                    placement={tooltipPlacement || 'right'}
                  >
                    {disabled ? <div>{action}</div> : action}
                  </Tooltip>
                ) : (
                  action
                );
              },
            )}
          </>
        )}
      </ActionsWrapper>
    </div>
  );
};

export default TableActions;
